import useAppInstallRequiredModal from '@/hooks/Cuj/useAppInstallRequiredModal';
import useInfiniteSearchNonF2fDoctors from '@/hooks/search/useInfiniteSearchNonF2fDoctors';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import { DepreactedCategoryType } from '@/services/doctor/DiagnosisCategoryService';
import {
  DoctorSortType,
  DoctorType,
  MedicalCategoryType,
  TelemedicineEligibleTargetType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import {
  IntegratedSearchTabType,
  NonF2fDoctorVo,
  RequestButtonType,
} from '@/types/search';
import { coreLogger } from '@/utils/logger';
import {
  NATIVE_POST_MESSAGE_TYPE,
  isWebView,
  sendPostMessageToNative,
} from '@/utils/mobile/RNPlugins';

interface Props {
  homeSearchCategory: { category: MedicalCategoryType; id: string } | null;
  keyword: string;
  tab: IntegratedSearchTabType;
}

export interface SearchDoctorViewModel {
  onClickDoctor: (doctorId: string) => void;
  handleClickRequestButton: () => void;
  moveToCategoryListScreen: () => void;
  keyword: string;
  doctors: Array<NonF2fDoctorVo>;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
}

const useSearchDoctorViewModel = ({
  homeSearchCategory,
  keyword,
  tab,
}: Props): SearchDoctorViewModel => {
  const { showModal, closeModal } = useModal();
  const { showModal: showAppInstallModal } = useAppInstallRequiredModal();
  const { moveToPage, pageName } = useCustomRouter();
  const { items, category, hasNextPage, isLoading, fetchNextPage } =
    useInfiniteSearchNonF2fDoctors(
      {
        request: {
          keyword: keyword,
          sortType: DoctorSortType.Random,
          page: 1,
          limit: 10,
          doctorType: DoctorType.Doctor,
          telemedicineEligibleTargetType:
            TelemedicineEligibleTargetType.NightAndHolidayOnly,
        },
      },
      { enabled: IntegratedSearchTabType.Doctor === tab && !!keyword },
    );

  const onClickDoctor = async (doctorId: string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: doctorId,
        tab: IntegratedSearchTabType.Doctor,
      },
    });

    if (isWebView()) {
      sendPostMessageToNative(
        NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_DETAIL_PAGE_AFTER_DIAGNOSIS_TARGET_CHECK,
        {
          doctorId: doctorId,
          ...(IntegratedSearchTabType.Doctor === tab
            ? category && {
                categoryId: Number(category.id),
                type:
                  category.category === MedicalCategoryType.Treatment
                    ? DepreactedCategoryType.Symptom
                    : DepreactedCategoryType.Subject,
              }
            : homeSearchCategory && {
                type:
                  homeSearchCategory.category === MedicalCategoryType.Treatment
                    ? DepreactedCategoryType.Symptom
                    : DepreactedCategoryType.Subject,
                categoryId: Number(homeSearchCategory.id),
              }),
        },
      );
      return;
    }

    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: '비대면 진료는 앱에서 할 수 있어요',
        message: '닥터나우 앱을 다운로드하고 사용해보세요',
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '닫기',
            onClick: () => closeModal(),
          },
          {
            type: MODAL_BUTTON_TYPE.PRIMARY,
            label: '다운로드',
            onClick: () => {
              coreLogger({
                eventName: 'search_doctor_item_click',
                pageName,
                payload: {
                  content_id: doctorId,
                  result_section: IntegratedSearchTabType.Doctor,
                  tab: IntegratedSearchTabType.Doctor,
                },
              });
              window.open('https://abr.ge/gtlbg3');
              closeModal();
            },
          },
        ],
        onClickOverlay: closeModal,
      },
    });
  };

  const moveToCategoryListScreen = () => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_CATEGORY_SELECT);
      return;
    }
    showAppInstallModal({
      title: '비대면 진료는 앱에서 확인할 수 있어요.',
      body: '진료 뿐만 아니라\n약 처방, 의료진 상담까지 가능해요!',
      installButtonText: '지금 설치하기',
      closeButtonText: '닫기',
    });
  };

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName,
      payload: {
        position: RequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_doctor',
      },
    });

    moveToPage({
      webLink: `${
        ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path
      }`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO
        .appScreenName,
      params: { keyword },
    });
  };

  return {
    doctors: items,
    hasNextPage: !!hasNextPage,
    isLoading: isLoading,
    fetchNextPage: fetchNextPage,
    onClickDoctor,
    handleClickRequestButton,
    moveToCategoryListScreen,
    keyword,
  };
};

export default useSearchDoctorViewModel;
